/**
 * EvolutionTierOverlay.jsx — full-screen celebration when a scribe crosses
 * a new evolution tier (every 10 levels). Spec: 3s locked, 10s auto-close,
 * confetti in tier colour, perk list, "do not re-show on refresh" — we ack
 * the server so `pendingTierAnnouncement` is cleared.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Star, X } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_HEX = {
  'Ink Novice': '#9CA3AF',
  'Wordsmith Apprentice': '#CD7F32',
  'Story Practitioner': '#B87333',
  'Narrative Adept': '#FFD700',
  'Craft Expert': '#10B981',
  'Master Scribe': '#3B82F6',
  'Sage Author': '#EF4444',
  'Luminary Storyteller': '#A855F7',
  'Grandmaster Visionary': '#67E8F9',
  'Legendary Archon': '#F9FAFB',
};

const TIER_PERKS = {
  'Wordsmith Apprentice': ['Genre paths unlocked (Romance, Thriller, Fantasy, Horror, Sci-Fi)'],
  'Story Practitioner':   ['Advanced paths unlocked', 'Editor-curated discovery boost'],
  'Narrative Adept':      ['Cross-genre fusion insights', 'Scribe-branded covers'],
  'Craft Expert':         ['🔐 Vault submission eligibility', 'Premium cover AI'],
  'Master Scribe':        ['Master paths unlocked', 'Featured-rail priority'],
  'Sage Author':          ['Mentor badge on profile', 'Personal publishing coach'],
  'Luminary Storyteller': ['Cross-platform promo'],
  'Grandmaster Visionary':['Elite paths unlocked', 'Revenue-share tier 2'],
  'Legendary Archon':     ['Platform ambassador title', 'Annual showcase feature'],
};

// Tiny CSS-only confetti — 40 absolutely-positioned squares, drifting on
// staggered keyframes. Avoids a heavy confetti library.
const Confetti = ({ colour }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 40 }).map((_, i) => ({
        key: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 1.8 + Math.random() * 1.6,
        size: 5 + Math.random() * 6,
        rotate: Math.random() * 360,
        tint: i % 3 === 0 ? '#ffffff' : colour,
      })),
    [colour]
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <span
          key={p.key}
          className="absolute top-[-10%] rounded-[2px] confetti-piece"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: p.tint,
            transform: `rotate(${p.rotate}deg)`,
            animation: `confetti-fall ${p.duration}s ${p.delay}s linear forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          to { transform: translateY(110vh) rotate(720deg); opacity: 0.2; }
        }
      `}</style>
    </div>
  );
};

const EvolutionTierOverlay = ({ announcement, onClose }) => {
  const [canDismiss, setCanDismiss] = useState(false);
  const autoCloseRef = useRef(null);
  const tier = announcement?.toTier;
  const colour = TIER_HEX[tier] || '#A855F7';
  const perks = TIER_PERKS[tier] || ['New capabilities unlocked'];

  useEffect(() => {
    const lockT = setTimeout(() => setCanDismiss(true), 3000);
    autoCloseRef.current = setTimeout(() => handleClose(), 10000);
    return () => {
      clearTimeout(lockT);
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = async () => {
    // Ack to server so it never re-shows.
    try {
      await authFetch(`${BACKEND_URL}/api/writer/tier-announcement/ack`, { method: 'POST' });
    } catch { /* silent — best-effort */ }
    onClose?.();
  };

  if (!announcement) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="evolution-overlay"
    >
      <Confetti colour={colour} />
      <div
        className="relative w-full max-w-lg bg-slate-900 border rounded-2xl shadow-2xl overflow-hidden"
        style={{ borderColor: `${colour}66` }}
      >
        <div
          className="relative p-6 text-center"
          style={{ background: `radial-gradient(circle at 50% 20%, ${colour}33 0%, transparent 70%)` }}
        >
          {canDismiss && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:bg-slate-800 hover:text-white"
              data-testid="evolution-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div
            className="mx-auto mb-5 w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-extrabold"
            style={{
              backgroundColor: `${colour}22`,
              border: `2px solid ${colour}`,
              boxShadow: `0 0 40px ${colour}55`,
              color: colour,
            }}
            data-testid="evolution-tier-badge"
          >
            <Sparkles className="w-10 h-10" />
          </div>
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Evolution Unlocked</p>
          <h2 className="text-3xl font-extrabold text-white mb-2" style={{ textShadow: `0 0 20px ${colour}66` }}>
            {tier}
          </h2>
          <p className="text-sm text-gray-300">
            Level {announcement.fromLevel} → <span className="font-bold text-white">Level {announcement.toLevel}</span>
          </p>
        </div>

        <div className="px-6 pb-6 pt-2 space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">New perks</p>
          <ul className="space-y-2">
            {perks.map((p, i) => (
              <li
                key={p}
                className="flex items-start gap-2 text-sm text-gray-200 opacity-0 animate-perk"
                style={{ animationDelay: `${0.6 + i * 0.25}s`, animationFillMode: 'forwards' }}
              >
                <Star className="w-4 h-4 mt-0.5 shrink-0" style={{ color: colour }} />
                {p}
              </li>
            ))}
          </ul>
          <div className="pt-3">
            <Button
              onClick={handleClose}
              disabled={!canDismiss}
              className="w-full disabled:opacity-50"
              style={{ backgroundColor: colour, color: '#0F172A' }}
              data-testid="evolution-continue"
            >
              {canDismiss ? 'Continue Writing' : 'Soak it in for a moment…'}
            </Button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes perk-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .animate-perk { animation: perk-in 0.35s ease-out; }
      `}</style>
    </div>
  );
};

export default EvolutionTierOverlay;
